/** Shared public-shell controls; product language comes from the backend. */
import { copy } from "./session";
import { ApiError } from "./api";
export function node<K extends keyof HTMLElementTagNameMap>(tag: K, text?: string, className?: string): HTMLElementTagNameMap[K] {
  const element = document.createElement(tag);
  if (text !== undefined) element.textContent = text;
  if (className) element.className = className;
  return element;
}
let toastTimer: ReturnType<typeof setTimeout> | undefined;
export function message(value: string): void {
  const toast = document.getElementById("toast");
  if (!toast) return;
  clearTimeout(toastTimer); toast.textContent = value; toast.classList.add("on");
  toastTimer = setTimeout(() => toast.classList.remove("on"), 6000);
}
export const errorText = (error: unknown): string => error instanceof ApiError ? error.message : copy().requestFailed;
export function feedback(element: HTMLElement, text: string, kind: "info" | "error" | "success" = "info"): void {
  element.textContent = text; element.dataset.kind = kind;
}
let activeAction: HTMLButtonElement | null = null;
export const actionOrigin = (): HTMLButtonElement | null => activeAction;
export function action(label: string, work: () => void | Promise<void>, primary = false): HTMLButtonElement {
  const button = node("button", label, primary ? "primary" : "ghost"); button.type = "button";
  button.onclick = () => {
    button.disabled = true;
    void Promise.resolve().then(() => { activeAction = button; try { return work(); } finally { activeAction = null; } }).catch((error: unknown) => message(errorText(error))).finally(() => { button.disabled = false; });
  };
  return button;
}
export function link(label: string, path: string, className = "nav-link"): HTMLAnchorElement {
  const a = node("a", label, className); a.href = path; return a;
}
let nextId = 0;
export function field(label: string, type = "text", autocomplete = "off", value = ""): { label: HTMLElement; input: HTMLInputElement } {
  const wrapper = node("div", undefined, "account-field"); const caption = node("label", label, "field");
  const input = node("input"); input.id = `account-field-${++nextId}`; input.name = autocomplete.split(" ")[0] || input.id;
  input.type = type; input.setAttribute("autocomplete", autocomplete); input.value = value; input.required = true;
  caption.htmlFor = input.id; wrapper.append(caption);
  if (type === "password") {
    const control = node("div", undefined, "password-control");
    const toggle = action(copy().showPassword, () => {
      const visible = input.type === "password"; input.type = visible ? "text" : "password";
      toggle.replaceChildren(icon(visible ? "eye-off" : "eye"), node("span", visible ? copy().hidePassword : copy().showPassword, "sr-only")); toggle.setAttribute("aria-pressed", String(visible));
    });
    toggle.replaceChildren(icon("eye"), node("span", copy().showPassword, "sr-only"));
    toggle.className = "password-toggle"; toggle.setAttribute("aria-controls", input.id); toggle.setAttribute("aria-pressed", "false");
    control.append(input, toggle); wrapper.append(control);
  } else wrapper.append(input);
  return { label: wrapper, input };
}
export function hint(input: HTMLInputElement, value: string): HTMLElement {
  const text = node("p", value, "hint field-hint"); text.id = `${input.id}-hint`; input.setAttribute("aria-describedby", text.id); return text;
}
export function form(title: string, subtitle?: string, level: "h1" | "h2" = "h1"): { container: HTMLElement; form: HTMLFormElement; status: HTMLElement } {
  const container = node("section", undefined, "account-card"); const heading = node(level, title); heading.tabIndex = -1; container.append(heading);
  if (subtitle) container.append(node("p", subtitle, "form-intro"));
  const form = node("form", undefined, "account-form"); const status = node("p", "", "form-status");
  status.setAttribute("role", "status"); status.setAttribute("aria-live", "polite"); container.append(form, status); return { container, form, status };
}
export function submit(form: HTMLFormElement, label: string, status: HTMLElement, work: () => Promise<void>, failed?: (error: unknown) => void): void {
  const button = node("button", label, "primary wide"); button.type = "submit"; form.append(button);
  form.onsubmit = (event) => {
    event.preventDefault(); if (button.disabled) return;
    button.disabled = true; form.setAttribute("aria-busy", "true"); feedback(status, copy().processing);
    void work().catch((error: unknown) => { feedback(status, errorText(error), "error"); failed?.(error); })
      .finally(() => { button.disabled = false; form.removeAttribute("aria-busy"); });
  };
}
export function installTheme(): void {
  const root = document.documentElement, button = document.getElementById("theme"); if (!button) return;
  try { const saved = localStorage.getItem("subty-theme"); if (saved === "light" || saved === "dark") root.dataset.theme = saved; } catch { /* Storage can be unavailable in private mode. */ }
  const paint = (): void => {
    const light = root.dataset.theme === "light";
    button.textContent = light ? "◐" : "◑";
    button.setAttribute("aria-label", light ? copy().themeDark : copy().themeLight); button.title = button.getAttribute("aria-label") ?? "";
  };
  button.onclick = () => { root.dataset.theme = root.dataset.theme === "light" ? "dark" : "light"; try { localStorage.setItem("subty-theme", root.dataset.theme); } catch { /* Theme still works without storage. */ } paint(); };
  paint();
}

/** Local functional icons, with names supplied by the control's text. */
export function icon(kind: "eye" | "eye-off" | "key"): SVGSVGElement {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("viewBox", "0 0 24 24"); svg.setAttribute("width", "18"); svg.setAttribute("height", "18"); svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("fill", "none"); svg.setAttribute("stroke", "currentColor"); svg.setAttribute("stroke-width", "1.5");
  const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
  path.setAttribute("d", kind === "key" ? "M14 14a6 6 0 1 0-4-4L3 17v4h4v-3h3l4-4Z M16 6h.01" : kind === "eye" ? "M2 12s4-7 10-7 10 7 10 7-4 7-10 7-10-7-10-7Z M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" : "M3 3l18 18 M10 5h2c6 0 10 7 10 7a20 20 0 0 1-3 4 M6 6a20 20 0 0 0-4 6s4 7 10 7a12 12 0 0 0 5-1 M10 10a3 3 0 0 0 4 4");
  svg.append(path); return svg;
}
